import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { ProjectRequest } from "../../../../../models/Project/Project";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { observer } from "mobx-react";
import {
  getOrderTypeFieldConfig,
  getPaymentFieldVisibilityFromLabel,
} from "./projectRequestFieldConfig";
import {
  ProjectRequestAction,
  ProjectRequestActionType,
  ProjectRequestStatusId,
} from "../../../../../models/Project/ProjectRequestStatus";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { formatPaymentTermLabel } from "../../../../../models/Project/ProjectRequestPayment";
import { OrderType } from "../../../../../models/Order/Order";

interface ProjectRequestViewPopupProps {
  open: boolean;
  request: ProjectRequest | null;
  onClose: () => void;
  onApproveOrReject: (
    requestId: number,
    action: ProjectRequestActionType,
  ) => void;
  canApproveReject: boolean;
}

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <CustomTypography
      variant="body3"
      style={{
        color: "inherit",
        fontWeight: 500,
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        opacity: 0.85,
      }}
    >
      {label}
    </CustomTypography>
    <CustomTypography variant="body2" style={{ lineHeight: 1.5 }}>
      {value ?? "--"}
    </CustomTypography>
  </Box>
);

const ProjectRequestViewPopup: React.FC<ProjectRequestViewPopupProps> = ({
  open,
  request,
  onClose,
  onApproveOrReject,
  canApproveReject,
}) => {
  const theme = useTheme();
  const { userStore, orderStore } = useStores();
  const actionsDisabled =
    Number(
      orderStore.existingOrder?.type_id ?? orderStore.orderRequest?.type_id,
    ) === OrderType.POD;

  const orderTypeFieldConfig = useMemo(
    () => getOrderTypeFieldConfig(request?.order_type_label),
    [request?.order_type_label],
  );

  const showPaymentTerm =
    orderTypeFieldConfig.show.payment_term &&
    getPaymentFieldVisibilityFromLabel(request?.payment_status_label)
      .showPaymentTerm;

  if (!request) return null;

  const status = request.status;
  const isPending = status === ProjectRequestStatusId.OPEN;
  const isApproved = status === ProjectRequestStatusId.APPROVED;
  const isRejected = status === ProjectRequestStatusId.REJECTED;
  const { showAmount, showPurchaseOrder } = getPaymentFieldVisibilityFromLabel(
    request.payment_status_label,
  );

  const handleClose = () => {
    onClose();
  };

  const handleApprove = () => {
    onApproveOrReject(request.id, ProjectRequestStatusId.APPROVED);
    handleClose();
  };

  const handleReject = () => {
    onApproveOrReject(request.id, ProjectRequestStatusId.REJECTED);
    handleClose();
  };

  const handleFinanceApprove = () => {
    onApproveOrReject(request.id, ProjectRequestAction.FINANCE_APPROVE);
  };

  const handleFinanceReject = () => {
    onApproveOrReject(request.id, ProjectRequestAction.FINANCE_REJECT);
  };

  const isFinanceApproved =
    request.finance_approval === 1 || request.finance_approval === true;
  const isFinanceRejected = request.finance_rejection === 1;
  const canShowFinanceActions =
    (isPending || isApproved) && !isFinanceApproved && !isFinanceRejected;
  const financeRejectionReason =
    request.rejection_reason_by_finance ?? request.rejection_reason;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "24px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Project Request #{request.id}
          </CustomTypography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Status & Decision
          </CustomTypography>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.background.paper,
              mb: 4,
            }}
          >
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              sx={{ mb: isApproved || isRejected ? 1 : 0 }}
            >
              <DetailRow label="Request Status" value={request.status_label} />
              <DetailRow label="Requested By" value={request.requestor_name} />
              <DetailRow
                label="Created Date"
                value={formatDate(request.created_date)}
              />
            </Box>
            {isApproved && (
              <>
                <Divider sx={{ my: 2 }} />
                <CustomTypography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.success.main ?? "#2e7d32",
                    marginBottom: 8,
                  }}
                >
                  Approved
                </CustomTypography>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                >
                  <DetailRow label="Approved By" value={request.closer_name} />
                  <DetailRow
                    label="Approved Date"
                    value={formatDate(request.closed_on)}
                  />
                </Box>
              </>
            )}
            {isRejected && (
              <>
                <Divider sx={{ my: 2 }} />
                <CustomTypography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.error.main,
                    marginBottom: 8,
                  }}
                >
                  Rejected
                </CustomTypography>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                >
                  <DetailRow label="Rejected By" value={request.closer_name} />
                  <DetailRow
                    label="Rejected Date"
                    value={formatDate(request.closed_on)}
                  />
                  <DetailRow
                    label="Reject Reason"
                    value={request.cancel_reason}
                  />
                </Box>
              </>
            )}
            {isFinanceApproved && (
              <>
                <Divider sx={{ my: 2 }} />
                <CustomTypography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.success.main ?? "#2e7d32",
                    marginBottom: 8,
                  }}
                >
                  Finance Approved
                </CustomTypography>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                >
                  <DetailRow
                    label="Approved By"
                    value={request.approved_by_finance_admin_name}
                  />
                  <DetailRow
                    label="Approved Date"
                    value={formatDate(request.finance_approval_date)}
                  />
                </Box>
              </>
            )}
            {isFinanceRejected && (
              <>
                <Divider sx={{ my: 2 }} />
                <CustomTypography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.error.main,
                    marginBottom: 8,
                  }}
                >
                  Finance Rejected
                </CustomTypography>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                >
                  <DetailRow
                    label="Rejected By"
                    value={request.rejected_by_finance_admin_name}
                  />
                  <DetailRow
                    label="Rejected Date"
                    value={formatDate(request.rejection_date_by_finance)}
                  />
                  <DetailRow
                    label="Reject Reason"
                    value={financeRejectionReason}
                  />
                </Box>
              </>
            )}
          </Box>
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Company & Contact
          </CustomTypography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            sx={{ mb: 3 }}
          >
            <DetailRow label="Company Name" value={request.company_name} />
            <DetailRow label="Brand Name" value={request.brand_name} />
            <DetailRow label="Client POC Name" value={request.client_poc} />
            <DetailRow
              label="Client POC Number"
              value={request.client_poc_contact_no}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Delivery & Dates
          </CustomTypography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            sx={{ mb: 3 }}
          >
            <DetailRow
              label="Expected Delivery Date"
              value={formatDateWithoutTime(request.delivery_date)}
            />
            <DetailRow
              label="Is Delivery Flexible"
              value={
                request.is_delivery_date_flexible
                  ? request.is_delivery_date_flexible === 1
                    ? "Yes"
                    : "No"
                  : "-"
              }
            />
            <DetailRow
              label="Mockup Status"
              value={request.mockup_status_label}
            />
            <DetailRow
              label="DIY Redemption"
              value={
                request.is_diy_redemption
                  ? request.is_diy_redemption === 0
                    ? "No"
                    : "Yes"
                  : "-"
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Shipment
          </CustomTypography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            sx={{ mb: 3 }}
          >
            <DetailRow
              label="Mode of Shipment"
              value={request.shipment_mode_label}
            />
            <DetailRow
              label="Type of Shipping"
              value={request.shipping_type_label}
            />
            <DetailRow
              label="Dispatch Date"
              value={formatDateWithoutTime(request.dispatch_date)}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Payment
          </CustomTypography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            sx={{ mb: 3 }}
          >
            <DetailRow
              label="Payment Status"
              value={request.payment_status_label}
            />
            {showPaymentTerm && (
              <DetailRow
                label="Payment Term"
                value={formatPaymentTermLabel(request.payment_term)}
              />
            )}
            {showPurchaseOrder && (
              <DetailRow
                label="Purchase Order Id"
                value={request.purchase_order}
              />
            )}
            {showAmount && <DetailRow label="Amount" value={request.amount} />}
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Order Type & Warehouse
          </CustomTypography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            sx={{ mb: 3 }}
          >
            <DetailRow label="Order Type" value={request.order_type_label} />
            <DetailRow
              label="Warehouse"
              value={request.warehouse_id ?? undefined}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <CustomTypography
            variant="heading6"
            style={{ fontWeight: 600, marginBottom: 16 }}
          >
            Order Notes
          </CustomTypography>
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.customColors.greyBackground,
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography
              variant="body2"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.6,
              }}
            >
              {request.order_additional_info || "--"}
            </CustomTypography>
          </Box>

          {canShowFinanceActions &&
            userStore.hasPermission([
              EmployeeRoles.FINANCE_TEAM_PROJECT_REQUEST_ACTIONS,
            ]) && (
              <>
                <Divider sx={{ my: 3 }} />
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Finance Decision
                </CustomTypography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                  <AppButton
                    variant="primary"
                    disabled={actionsDisabled}
                    onClick={handleFinanceApprove}
                    label="Finance Approved"
                  />
                  {/* <AppButton
                      variant="secondary"
                      onClick={handleFinanceReject}
                      label="Finance Reject"
                    /> */}
                </Box>
              </>
            )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          {isPending && (canApproveReject || actionsDisabled) && (
            <>
              <AppButton
                variant="primary"
                disabled={actionsDisabled || !canApproveReject}
                onClick={handleApprove}
                label="Approve"
              />
              <AppButton
                variant="secondary"
                disabled={actionsDisabled || !canApproveReject}
                onClick={handleReject}
                label="Reject"
              />
            </>
          )}
          <AppButton variant="grayscale" onClick={handleClose} label="Close" />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(ProjectRequestViewPopup);
